<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('customer.contact.customersContacts') }}
        </span>
        <h3 class="page-title">
          {{ $t('customer.contact.addNewTitle') }}
        </h3>
      </d-col>
      <d-col
        col
        sm="6"
        class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          @click.prevent="$router.push('/kontakty-z-klientami')"
          class="mr-1">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>
        <d-button
          theme="success"
          @click.prevent="validateForm">
          <i class="material-icons">add</i>
          {{ $t('customer.contact.addNewLabel') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <validation-observer ref="contact-new-form" tag="form">
        <d-card class="mb-5">
          <d-tabs
            v-if="!loadError && allDataLoaded"
            v-sticky-tabs
            card >
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom">
                {{ $t('ui.generalInfo') }}
              </h4>

              <div class="form-group">
                <label>
                  {{ $t('customer.contact.contactDate') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="date"
                  v-slot="{ errors }">
                  <d-datepicker
                    v-model="date"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :disabled-dates="futureDates"
                    :language="translationsForDatePicker" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label>
                  {{ $t('users.customer') }}:
                </label>

                <select-customer
                  :customerID="customer_id" />

                <div
                  v-if="triedToSend && !customerIsValid"
                  class="text-danger">
                  {{ $t('ui.thisFieldIsRequired') }}
                </div>
              </div>

              <div
                v-if="customer_id"
                class="form-group">
                <label for="name">
                  {{ $t('customer.contact.contactRelatedToOrder') }}:
                </label>

                <select-order
                  ref="orderSelect"
                  :orderID="null"
                  :customerID="customer_id"/>
              </div>

              <div
                v-if="customer_id"
                class="form-group">
                <label for="name">
                  {{ $t('customer.contact.contactRelatedToContact') }}:
                </label>

                <contact-customer-contact
                  ref="contactSelect"
                  :customerId="customer_id"
                  :readOnly="false" />
              </div>

              <div class="form-group">
                <label class="d-block">
                  {{ $t('customer.contact.contactType') }}:
                </label>
                <d-form-checkbox
                  v-for="(contactType, index) of contactTypes"
                  :key="'contact-type-checkbox-' + index"
                  inline
                  v-model="types"
                  :value="contactType.type">
                  {{ contactType.name }}
                </d-form-checkbox>
              </div>

              <div class="form-group">
                <label for="name">
                  {{ $t('customer.contact.descriptionOptional') }}:
                </label>

                <d-textarea
                  :value.prop="description"
                  v-model="description" />
              </div>
            </d-tab>

            <d-tab
              :title="$t('customer.contact.notifications')">
              <d-alert
                theme="warning"
                show>
                {{ $t('customer.contact.notificationsWillBeAvailableAfterSaving') }}
              </d-alert>
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded && !loadError" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>
  </d-container>
</template>

<script>
import ContactCustomerContact from './ContactCustomerContact.vue';
import datepickerTranslations from './../../data/datepicker-translations.js';
import FormUtils from './../../utils/FormUtils.js';
import SelectCustomer from './../../components/common/SelectCustomer.vue';
import SelectOrder from './../../components/common/SelectOrder.vue';
import Vue from 'vue';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'contact-new',
  components: {
    'contact-customer-contact': ContactCustomerContact,
    'select-customer': SelectCustomer,
    'select-order': SelectOrder
  },
  computed: {
    allDataLoaded () {
      return this.loadedTypes;
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    futureDates () {
      return {
        from: new Date()
      };
    }
  },
  watch: {
    'customer_id': function (newValue) {
      if (newValue) {
        this.customerIsValid = true;
      } else {
        this.customerIsValid = false;
      }
    }
  },
  data() {
    return {
      date: new Date(),
      customer_id: null,
      customer_contact_id: null,
      order_id: null,
      contactTypes: [],
      types: ['PHONE'],
      description: '',
      loadError: false,
      loadedTypes: false,
      customerIsValid: false,
      triedToSend: false
    };
  },
  mounted () {
    this.loadAdditionalData();
    this.$bus.$on('ui-select-customer-updated', this.updateCustomer);
    this.$bus.$on('ui-select-order-updated', this.updateOrder);
    this.$bus.$on('ui-select-customer-contact-updated', this.updateCustomerContact);
  },
  methods: {
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-contact-types/items',
        loadedKey: 'loadedTypes',
        outputKey: 'contactTypes',
        errorKey: 'loadError'
      });
    },
    validateForm () {
      this.triedToSend = true;

      if (!this.customer_id) {
        return;
      }

      FormUtils.validate(this.$refs['contact-new-form'], this.submitForm);
    },
    submitForm () {
      let formDataToSend = {
        date: [this.date.getFullYear(), this.date.getMonth() + 1, this.date.getDate()].map(n => n < 10 ? '0' + n : n).join('-'),
        customer_id: this.customer_id,
        types: this.types,
        description: this.description
      };

      if (this.order_id) {
        formDataToSend.order_id = parseInt(this.order_id, 10);
      }

      if (this.customer_contact_id) {
        formDataToSend.customer_contact_id = this.customer_contact_id;
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/contacts/store',
        formData: formDataToSend,
        successTitle: this.$t('customer.contact.addContactSuccessTitle'),
        successText: this.$t('customer.contact.addContactSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.contact.addContactErrorText'),
        successAction: () => {
          this.$router.push('/kontakty-z-klientami/');
        }
      });
    },
    updateCustomer (newID) {
      this.customer_id = newID;

      if (newID) {
        Vue.nextTick(() => {
          this.$refs['orderSelect'].resetValue();
          this.$refs['orderSelect'].loadOrdersForCustomer();
          this.$refs['contactSelect'].fetchContacts();
        });
      } else {
        this.order_id = null;
      }
    },
    updateOrder (newID) {
      this.order_id = newID;
    },
    updateCustomerContact (newID) {
      this.customer_contact_id = newID;
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-customer-updated', this.updateCustomer);
    this.$bus.$off('ui-select-order-updated', this.updateOrder);
    this.$bus.$off('ui-select-customer-contact-updated', this.updateCustomerContact);
  }
};
</script>
